'use client'

import React, { FC, useMemo, useRef, useState } from 'react'

import clsx from 'clsx'
import { useClickAway } from 'react-use'

import { Switch } from '@style-port/roov-ui-components-react'
import { Icon, Txt } from 'src/components/atoms'

import { useGetCustomRoles } from 'src/hooks'
import { CustomRoleEntity } from 'src/types'

import styles from './selectCustomRoleDropdown.module.scss'

type Props = {
  alreadyAssignedCustomRoleIds: number[]
  selectCustomRole: (customRole: CustomRoleEntity) => void
  removeCustomRole: (customRole: CustomRoleEntity) => void
  className?: string
}

export const SelectCustomRoleDropdown: FC<Props> = ({
  alreadyAssignedCustomRoleIds,
  selectCustomRole,
  removeCustomRole,
  className,
}) => {
  const [isFocus, setIsFocus] = useState(false)
  const [showSearchUsers, setShowSearchUsers] = useState(false)

  const { customRoles } = useGetCustomRoles()

  const handleOnFocusInput = () => {
    setIsFocus(true)
    setShowSearchUsers(true)
  }

  const selectedCustomRoles = useMemo(() => {
    return customRoles.filter(customRole => alreadyAssignedCustomRoleIds.includes(customRole.id))
  }, [alreadyAssignedCustomRoleIds, customRoles])

  const unselectedCustomRoles = useMemo(() => {
    return customRoles.filter(customRole => !alreadyAssignedCustomRoleIds.includes(customRole.id))
  }, [alreadyAssignedCustomRoleIds, customRoles])

  const ref = useRef(null)
  useClickAway(ref, () => {
    if (showSearchUsers) {
      setShowSearchUsers(false)
    }
  })

  return (
    <div className={className}>
      <div className="tw-relative">
        <div
          className={clsx(
            styles.container,
            'tw-border tw-border-border-secondary tw-bg-surface-primary',
            isFocus && 'tw-border tw-border-roovPrimary-default !tw-rounded-[0.29rem_0.29rem_0_0]'
          )}
        >
          <ul className="tw-flex tw-gap-s-1 tw-w-full tw-flex-wrap">
            {selectedCustomRoles.map(customRole => (
              <li key={customRole.id}>
                <Switch
                  switchType="primary"
                  onClick={() => removeCustomRole(customRole)}
                  className="tw-w-full"
                >
                  <div className="tw-flex tw-items-center tw-gap-s-3">
                    <Txt>{customRole.name}</Txt>
                    <Icon path="close/soft/line" size="--r1" />
                  </div>
                </Switch>
              </li>
            ))}
            <li className={clsx(selectedCustomRoles.length === 0 ? 'tw-w-full' : 'tw-w-[20rem]')}>
              <input
                placeholder="カスタムロールを選択"
                onFocus={handleOnFocusInput}
                onBlur={() => setIsFocus(false)}
                className="tw-caret-correctGreen-default tw-outline-none tw-resize-none tw-h-[2rem] tw-leading-[2rem] tw-w-full"
              />
            </li>
          </ul>
        </div>
        {showSearchUsers && (
          <div className="tw-absolute tw-z-30 tw-w-full tw-bg-surface-secondary tw-p-s-1 tw-rounded-[0_0_0.29rem_0.29rem]">
            <Txt sizeType="s" className="tw-font-bold tw-text-standardText-primary">
              作成済のカスタムロール
            </Txt>
            <div ref={ref}>
              {customRoles.length === 0 ? (
                <Txt className="tw-text-standardText-secondary tw-mt-s-1">
                  カスタムロールが未作成です。全権管理設定で作成してください。
                </Txt>
              ) : (
                <ul className="tw-flex tw-gap-s-1 tw-mt-s-2 tw-flex-wrap">
                  {unselectedCustomRoles.map(customRole => (
                    <li key={customRole.id}>
                      <Switch
                        switchType="primary"
                        onClick={() => selectCustomRole(customRole)}
                        className="tw-w-full"
                      >
                        <Txt>{customRole.name}</Txt>
                      </Switch>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
